import { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { X, Phone, Check, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { OPEN_CALLBACK_EVENT } from '@/lib/callback';

/*
  Rückruf statt Telefonnummer: Die "Call"-Buttons zeigten auf einen toten
  cal.com-Link, danach direkt auf Juliens Handynummer. Jetzt hinterlässt der
  Besucher Name + Nummer, die Anfrage läuft über /api/contact (Mail + CRM),
  und Julien ruft zurück, wann es passt.

  Geöffnet wird das Fenster von überall per openCallback() (lib/callback) — ein globales
  Event, damit die Buttons in Footer, Blog, Sticky-Leiste usw. keinen
  gemeinsamen State brauchen.
*/

const isValidPhone = (v: string) => /^[+]?[\d\s/().-]{6,}$/.test(v.trim());

const TIME_SLOTS = ['Egal', 'Vormittags', 'Mittags', 'Nachmittags', 'Abends'];

const CallbackModal = () => {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [slot, setSlot] = useState('Egal');
  const [submitState, setSubmitState] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');

  useEffect(() => {
    const onOpen = () => setOpen(true);
    window.addEventListener(OPEN_CALLBACK_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_CALLBACK_EVENT, onOpen);
  }, []);

  const handleClose = useCallback(() => {
    setOpen(false);
    setTimeout(() => {
      setName('');
      setPhone('');
      setSlot('Egal');
      setSubmitState('idle');
    }, 300);
  }, []);

  // Escape to close + scroll lock
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleClose();
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, handleClose]);

  const valid = name.trim().length >= 2 && isValidPhone(phone);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!valid) return;
    setSubmitState('loading');
    try {
      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: name.trim(),
          phone: phone.trim(),
          project: 'Rückruf',
          message: `Rückrufwunsch — passt am besten: ${slot}`,
        }),
      });
      setSubmitState(res.ok ? 'success' : 'error');
    } catch {
      setSubmitState('error');
    }
  };

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 animate-fade-in"
      role="dialog"
      aria-modal="true"
      aria-label="Rückruf anfordern"
    >
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={handleClose} />

      <div className="relative w-full max-w-md bg-card border border-border/60 rounded-2xl shadow-2xl overflow-hidden">
        <div
          className="absolute inset-x-0 top-0 h-px"
          style={{ background: 'linear-gradient(90deg, transparent, hsl(var(--primary)), transparent)' }}
        />

        <button
          onClick={handleClose}
          className="absolute top-3 right-3 z-10 w-11 h-11 flex items-center justify-center rounded-lg text-muted-foreground hover:text-foreground hover:bg-secondary/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary transition-colors"
          aria-label="Schließen"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="relative p-8 md:p-10">
          {submitState === 'success' ? (
            <div role="status" className="text-center space-y-4 py-4">
              <div className="mx-auto w-12 h-12 rounded-full bg-primary/15 flex items-center justify-center">
                <Check className="w-6 h-6 text-primary" />
              </div>
              <h2 className="text-xl font-semibold">Alles klar, {name.trim().split(' ')[0]}.</h2>
              <p className="text-sm text-muted-foreground">
                Julien ruft dich werktags innerhalb von 24 h zurück
                {slot !== 'Egal' ? ` — ${slot.toLowerCase()}` : ''}.
              </p>
              <Button onClick={handleClose} variant="outline" className="mt-2">
                Schließen
              </Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <p className="uppercase tracking-[0.25em] text-xs font-medium text-primary mb-2">Rückruf</p>
                <h2 className="text-2xl font-semibold">Lieber kurz sprechen?</h2>
                <p className="mt-2 text-sm text-muted-foreground">
                  Hinterlass deine Nummer, Julien ruft dich persönlich zurück. Kein Verkaufsgespräch.
                </p>
              </div>

              <div className="space-y-2">
                <label htmlFor="cb-name" className="block text-sm font-medium">Name</label>
                <Input
                  id="cb-name"
                  autoComplete="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Wie heißt du?"
                  required
                />
              </div>

              <div className="space-y-2">
                <label htmlFor="cb-phone" className="block text-sm font-medium">Telefonnummer</label>
                <Input
                  id="cb-phone"
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="0170 1234567"
                  required
                />
              </div>

              <fieldset className="space-y-2">
                <legend className="block text-sm font-medium mb-2">Wann passt es dir?</legend>
                <div className="flex flex-wrap gap-2">
                  {TIME_SLOTS.map((t) => (
                    <button
                      key={t}
                      type="button"
                      onClick={() => setSlot(t)}
                      aria-pressed={slot === t}
                      className={`min-h-10 px-3 rounded-md border text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary ${
                        slot === t
                          ? 'border-primary bg-primary/15 text-foreground'
                          : 'border-border/60 text-muted-foreground hover:text-foreground'
                      }`}
                    >
                      {t}
                    </button>
                  ))}
                </div>
              </fieldset>

              {submitState === 'error' && (
                <p className="text-sm text-red-400">
                  Das hat nicht geklappt. Versuch's nochmal oder schreib an info@beyerdigital.de.
                </p>
              )}

              <Button type="submit" size="lg" className="w-full" disabled={!valid || submitState === 'loading'}>
                {submitState === 'loading' ? (
                  <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
                ) : (
                  <Phone className="w-4 h-4" aria-hidden="true" />
                )}
                Rückruf anfordern
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default CallbackModal;
